import { z } from 'zod';
import type { DynamicFormField } from '@/features/dynamic-forms';

type T = (key: string) => string;

/**
 * Build a Zod schema dynamically from a dynamic form's field definitions.
 * Returns a schema that validates `form_responses` as a `Record<string, unknown>`.
 */
export function buildDynamicFormSchema(fields: DynamicFormField[], t: T) {
  const shape: Record<string, z.ZodTypeAny> = {};

  for (const field of fields) {
    const key = String(field.id);
    let fieldSchema: z.ZodTypeAny;

    switch (field.type) {
      case 'short_text':
      case 'long_text':
        fieldSchema = field.is_required
          ? z.string().trim().min(1, t('fieldRequired'))
          : z.string().trim().optional().or(z.literal(''));
        break;

      case 'number':
        fieldSchema = field.is_required
          ? z.coerce.number({ message: t('fieldRequired') })
          : z.coerce.number().optional().or(z.literal(''));
        break;

      case 'date':
        fieldSchema = field.is_required
          ? z.string().min(1, t('fieldRequired'))
          : z.string().optional().or(z.literal(''));
        break;

      case 'selection_list':
        if (field.is_multi_select) {
          fieldSchema = field.is_required
            ? z.array(z.string()).min(1, t('fieldRequired'))
            : z.array(z.string()).optional().default([]);
        } else {
          fieldSchema = field.is_required
            ? z.string().min(1, t('fieldRequired'))
            : z.string().optional().or(z.literal(''));
        }
        break;

      case 'yes_no':
        fieldSchema = z.boolean().default(false);
        break;

      default:
        fieldSchema = z.unknown().optional();
    }

    shape[key] = fieldSchema;
  }

  return z.object(shape);
}

export type DynamicFormResponseValues = Record<string, unknown>;

/**
 * Build the full registration form schema: system fields + dynamic form fields.
 */
export function buildRegistrationFormSchema(fields: DynamicFormField[], t: T) {
  return z.object({
    container_type_id: z.coerce
      .number({ message: t('fieldRequired') })
      .positive(t('fieldRequired')),
    form_responses: buildDynamicFormSchema(fields, t)
  });
}
